import { supabase, handleSupabaseError, handleSupabaseSuccess } from '../lib/supabase';
import { DateRangeFilter, ApiResponse } from '../types';

export type Granularity = 'daily' | 'weekly' | 'monthly';

export interface OverviewStats {
  totalUsers: number;
  newUsersToday: number;
  newUsersThisWeek: number;
  newUsersThisMonth: number;
  totalCreators: number;
  pendingCreatorApplications: number;
  totalVideos: number;
  publishedVideos: number;
  totalQuizzes: number;
  quizAttemptsToday: number;
  quizAbandonedToday: number;
  quizOpenedToday: number;
  vocabLearnedToday: number;
  vocabSavedToday: number;
}

export interface UserGrowthPoint {
  date: string;
  students: number;
  creators: number;
  total: number;
}

export interface EngagementMetrics {
  videosWatched: number;
  avgWatchCompletion: number;
  totalQuizAttempts: number;
  quizCompletionRate: number;
  avgQuizScore: number;
  vocabularyWordsLearned: number;
  vocabularyWordsSaved: number;
  commentsPosted: number;
  likesGiven: number;
}

export interface ContentAnalytics {
  videosBySourceType: { sourceType: string; count: number }[];
  contentGenerationStatus: {
    quizGenerated: number;
    vocabularyGenerated: number;
    subtitlesGenerated: number;
    quizNotGenerated: number;
    vocabularyNotGenerated: number;
    subtitlesNotGenerated: number;
  };
}

export interface TopCreator {
  id: string;
  name: string;
  videoCount: number;
}

export interface TopWatchedVideo {
  videoId: string;
  title: string;
  watchCount: number;
}

export interface SubscriptionStats {
  free: number;
  premium: number;
  expiringSoon: { id: string; email: string; expiresAt: string }[];
}

class AnalyticsService {
  private static instance: AnalyticsService;

  private constructor() {}

  public static getInstance(): AnalyticsService {
    if (!AnalyticsService.instance) {
      AnalyticsService.instance = new AnalyticsService();
    }
    return AnalyticsService.instance;
  }

  async getOverviewStats(): Promise<ApiResponse<OverviewStats>> {
    try {
      const now = new Date();
      const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
      const weekStart = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();

      const [
        totalUsersRes,
        newUsersTodayRes,
        newUsersWeekRes,
        newUsersMonthRes,
        totalCreatorsRes,
        pendingAppsRes,
        totalVideosRes,
        publishedVideosRes,
        totalQuizzesRes,
        quizAttemptsTodayRes,
        quizAbandonedTodayRes,
        quizOpenedTodayRes,
        vocabLearnedTodayRes,
        vocabSavedTodayRes,
      ] = await Promise.all([
        supabase.from('profiles').select('*', { count: 'exact', head: true }),
        supabase.from('profiles').select('*', { count: 'exact', head: true }).gte('created_at', todayStart),
        supabase.from('profiles').select('*', { count: 'exact', head: true }).gte('created_at', weekStart),
        supabase.from('profiles').select('*', { count: 'exact', head: true }).gte('created_at', monthStart),
        supabase.from('profiles').select('*', { count: 'exact', head: true }).eq('role', 'teacher'),
        supabase.from('profiles').select('*', { count: 'exact', head: true }).eq('role', 'teacher').eq('application_status', 'pending'),
        supabase.from('videos').select('*', { count: 'exact', head: true }),
        supabase.from('videos').select('*', { count: 'exact', head: true }).eq('is_published', true),
        supabase.from('quizzes').select('*', { count: 'exact', head: true }),
        supabase.from('quiz_attempts').select('*', { count: 'exact', head: true }).gte('created_at', todayStart),
        supabase.from('quiz_attempts').select('*', { count: 'exact', head: true }).gte('created_at', todayStart).eq('status', 'abandoned'),
        supabase.from('quiz_views').select('*', { count: 'exact', head: true }).gte('created_at', todayStart),
        supabase.from('vocabulary_button_clicks').select('*', { count: 'exact', head: true }).gte('created_at', todayStart),
        supabase.from('vocabulary_save_clicks').select('*', { count: 'exact', head: true }).gte('created_at', todayStart).eq('action', 'save'),
      ]);

      return handleSupabaseSuccess({
        totalUsers: totalUsersRes.count || 0,
        newUsersToday: newUsersTodayRes.count || 0,
        newUsersThisWeek: newUsersWeekRes.count || 0,
        newUsersThisMonth: newUsersMonthRes.count || 0,
        totalCreators: totalCreatorsRes.count || 0,
        pendingCreatorApplications: pendingAppsRes.count || 0,
        totalVideos: totalVideosRes.count || 0,
        publishedVideos: publishedVideosRes.count || 0,
        totalQuizzes: totalQuizzesRes.count || 0,
        quizAttemptsToday: quizAttemptsTodayRes.count || 0,
        quizAbandonedToday: quizAbandonedTodayRes.count || 0,
        quizOpenedToday: quizOpenedTodayRes.count || 0,
        vocabLearnedToday: vocabLearnedTodayRes.count || 0,
        vocabSavedToday: vocabSavedTodayRes.count || 0,
      });
    } catch (error) {
      return handleSupabaseError<OverviewStats>(error);
    }
  }

  async getUserGrowthData(
    dateRange?: DateRangeFilter,
    granularity: Granularity = 'daily'
  ): Promise<ApiResponse<UserGrowthPoint[]>> {
    try {
      const endDate = dateRange?.endDate || new Date();
      const startDate = dateRange?.startDate || new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);

      const { data, error } = await supabase
        .from('profiles')
        .select('created_at, role')
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString())
        .order('created_at', { ascending: true });

      if (error) return handleSupabaseError<UserGrowthPoint[]>(error);

      const grouped = new Map<string, { students: number; creators: number }>();

      for (const profile of data || []) {
        const key = this.getDateKey(new Date(profile.created_at), granularity);
        if (!grouped.has(key)) {
          grouped.set(key, { students: 0, creators: 0 });
        }
        const entry = grouped.get(key)!;
        if (profile.role === 'teacher') {
          entry.creators++;
        } else {
          entry.students++;
        }
      }

      const result: UserGrowthPoint[] = Array.from(grouped.entries())
        .map(([date, counts]) => ({
          date,
          students: counts.students,
          creators: counts.creators,
          total: counts.students + counts.creators,
        }))
        .sort((a, b) => a.date.localeCompare(b.date));

      return handleSupabaseSuccess(result);
    } catch (error) {
      return handleSupabaseError<UserGrowthPoint[]>(error);
    }
  }

  async getEngagementMetrics(): Promise<ApiResponse<EngagementMetrics>> {
    try {
      const [
        videosWatchedRes,
        watchDataRes,
        totalAttemptsRes,
        completedAttemptsRes,
        scoreDataRes,
        vocabLearnedRes,
        vocabSavedRes,
        commentsRes,
        likesRes,
      ] = await Promise.all([
        supabase.from('video_progress').select('*', { count: 'exact', head: true }),
        supabase.from('video_progress').select('completion_percentage').limit(10000),
        supabase.from('quiz_attempts').select('*', { count: 'exact', head: true }),
        supabase.from('quiz_attempts').select('*', { count: 'exact', head: true }).eq('status', 'completed'),
        supabase.from('quiz_attempts').select('score_percentage').not('score_percentage', 'is', null).limit(10000),
        supabase.from('vocabulary_button_clicks').select('*', { count: 'exact', head: true }),
        supabase.from('vocabulary_save_clicks').select('*', { count: 'exact', head: true }).eq('action', 'save'),
        supabase.from('video_comments').select('*', { count: 'exact', head: true }),
        supabase.from('video_likes').select('*', { count: 'exact', head: true }),
      ]);

      const watchData = watchDataRes.data || [];
      const avgWatchCompletion = watchData.length
        ? watchData.reduce((sum: number, r: any) => sum + (r.completion_percentage || 0), 0) / watchData.length
        : 0;

      const scoreData = scoreDataRes.data || [];
      const avgQuizScore = scoreData.length
        ? scoreData.reduce((sum: number, r: any) => sum + (r.score_percentage || 0), 0) / scoreData.length
        : 0;

      const totalAttempts = totalAttemptsRes.count || 0;
      const completedAttempts = completedAttemptsRes.count || 0;
      const quizCompletionRate = totalAttempts > 0
        ? (completedAttempts / totalAttempts) * 100
        : 0;

      return handleSupabaseSuccess({
        videosWatched: videosWatchedRes.count || 0,
        avgWatchCompletion: Math.round(avgWatchCompletion * 100) / 100,
        totalQuizAttempts: totalAttempts,
        quizCompletionRate: Math.round(quizCompletionRate * 100) / 100,
        avgQuizScore: Math.round(avgQuizScore * 100) / 100,
        vocabularyWordsLearned: vocabLearnedRes.count || 0,
        vocabularyWordsSaved: vocabSavedRes.count || 0,
        commentsPosted: commentsRes.count || 0,
        likesGiven: likesRes.count || 0,
      });
    } catch (error) {
      return handleSupabaseError<EngagementMetrics>(error);
    }
  }

  async getContentAnalytics(): Promise<ApiResponse<ContentAnalytics>> {
    try {
      const { data: videos, error } = await supabase
        .from('videos')
        .select('source_type, quiz_generated, vocabulary_generated, subtitles_generated');

      if (error) return handleSupabaseError<ContentAnalytics>(error);

      const sourceTypeCounts = new Map<string, number>();
      let quizGen = 0, quizNotGen = 0;
      let vocabGen = 0, vocabNotGen = 0;
      let subsGen = 0, subsNotGen = 0;

      for (const v of videos || []) {
        const st = v.source_type || 'unknown';
        sourceTypeCounts.set(st, (sourceTypeCounts.get(st) || 0) + 1);

        if (v.quiz_generated) quizGen++; else quizNotGen++;
        if (v.vocabulary_generated) vocabGen++; else vocabNotGen++;
        if (v.subtitles_generated) subsGen++; else subsNotGen++;
      }

      return handleSupabaseSuccess({
        videosBySourceType: Array.from(sourceTypeCounts.entries()).map(
          ([sourceType, count]) => ({ sourceType, count })
        ),
        contentGenerationStatus: {
          quizGenerated: quizGen,
          vocabularyGenerated: vocabGen,
          subtitlesGenerated: subsGen,
          quizNotGenerated: quizNotGen,
          vocabularyNotGenerated: vocabNotGen,
          subtitlesNotGenerated: subsNotGen,
        },
      });
    } catch (error) {
      return handleSupabaseError<ContentAnalytics>(error);
    }
  }

  async getTopCreators(limit: number = 10): Promise<ApiResponse<TopCreator[]>> {
    try {
      const { data: videos, error: videoError } = await supabase
        .from('videos')
        .select('creator_id');

      if (videoError) return handleSupabaseError<TopCreator[]>(videoError);

      const videoCounts = new Map<string, number>();
      for (const v of videos || []) {
        videoCounts.set(v.creator_id, (videoCounts.get(v.creator_id) || 0) + 1);
      }

      const topCreatorIds = Array.from(videoCounts.entries())
        .sort((a, b) => b[1] - a[1])
        .slice(0, limit);

      if (topCreatorIds.length === 0) return handleSupabaseSuccess([]);

      const { data: profiles, error: profileError } = await supabase
        .from('profiles')
        .select('id, full_name')
        .in('id', topCreatorIds.map(([id]) => id));

      if (profileError) return handleSupabaseError<TopCreator[]>(profileError);

      const nameMap = new Map((profiles || []).map(p => [p.id, p.full_name || 'Unknown']));

      const result: TopCreator[] = topCreatorIds.map(([id, videoCount]) => ({
        id,
        name: nameMap.get(id) || 'Unknown',
        videoCount,
      }));

      return handleSupabaseSuccess(result);
    } catch (error) {
      return handleSupabaseError<TopCreator[]>(error);
    }
  }

  async getTopWatchedVideos(limit: number = 10): Promise<ApiResponse<TopWatchedVideo[]>> {
    try {
      const { data: progress, error } = await supabase
        .from('video_progress')
        .select('video_id');

      if (error) return handleSupabaseError<TopWatchedVideo[]>(error);

      const watchCounts = new Map<string, number>();
      for (const p of progress || []) {
        watchCounts.set(p.video_id, (watchCounts.get(p.video_id) || 0) + 1);
      }

      const topVideoIds = Array.from(watchCounts.entries())
        .sort((a, b) => b[1] - a[1])
        .slice(0, limit);

      if (topVideoIds.length === 0) return handleSupabaseSuccess([]);

      const { data: videos, error: videoError } = await supabase
        .from('videos')
        .select('id, title')
        .in('id', topVideoIds.map(([id]) => id));

      if (videoError) return handleSupabaseError<TopWatchedVideo[]>(videoError);

      const titleMap = new Map((videos || []).map(v => [v.id, v.title]));

      const result: TopWatchedVideo[] = topVideoIds.map(([videoId, watchCount]) => ({
        videoId,
        title: titleMap.get(videoId) || 'Unknown Video',
        watchCount,
      }));

      return handleSupabaseSuccess(result);
    } catch (error) {
      return handleSupabaseError<TopWatchedVideo[]>(error);
    }
  }

  async getSubscriptionStats(): Promise<ApiResponse<SubscriptionStats>> {
    try {
      const now = new Date();
      const thirtyDaysOut = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);

      const [freeRes, premiumRes, expiringRes] = await Promise.all([
        supabase
          .from('profiles')
          .select('*', { count: 'exact', head: true })
          .or('subscription_tier.is.null,subscription_tier.eq.free'),
        supabase
          .from('profiles')
          .select('*', { count: 'exact', head: true })
          .eq('subscription_tier', 'premium'),
        supabase
          .from('profiles')
          .select('id, email, subscription_expires_at')
          .eq('subscription_tier', 'premium')
          .not('subscription_expires_at', 'is', null)
          .gte('subscription_expires_at', now.toISOString())
          .lte('subscription_expires_at', thirtyDaysOut.toISOString())
          .order('subscription_expires_at', { ascending: true })
          .limit(20),
      ]);

      return handleSupabaseSuccess({
        free: freeRes.count || 0,
        premium: premiumRes.count || 0,
        expiringSoon: (expiringRes.data || []).map((p: any) => ({
          id: p.id,
          email: p.email,
          expiresAt: p.subscription_expires_at!,
        })),
      });
    } catch (error) {
      return handleSupabaseError<SubscriptionStats>(error);
    }
  }

  private getDateKey(date: Date, granularity: Granularity): string {
    switch (granularity) {
      case 'daily':
        return date.toISOString().split('T')[0];
      case 'weekly': {
        const weekStart = new Date(date);
        weekStart.setDate(date.getDate() - date.getDay());
        return weekStart.toISOString().split('T')[0];
      }
      case 'monthly':
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      default:
        return date.toISOString().split('T')[0];
    }
  }
}

export const analyticsService = AnalyticsService.getInstance();
